import groupBy from 'lodash/groupBy';
import orderBy from 'lodash/orderBy';
import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  currentClientIdSelector,
  groupAccountsBySelector,
} from '../../../../CommonSelectors/CommonAreaSelector';
import { AsyncComponentHookResult } from '../../../../Types/ComponentHookResult';
import {
  EarmarkOptionsDTO,
  OptionsDTO,
  useAccountsOptionsQuery,
} from '../../api/useAccountsOptionsQuery';
import {
  AccountDTO,
  AccountEarmarkDTO,
  AccountTypeCategories,
  AccountTypeCategoryDTO,
  useAccountsQuery,
} from '../../api/useAccountsQuery';
import { ALL_GOALS, EARMARK_ICONS, FALLBACK_ICON } from './consts';

enum GroupOptions {
  AccountTypeCategory,
  InstitutionName,
}
type GroupPossibleKeys = keyof typeof GroupOptions;
interface Earmark extends AccountEarmarkDTO {
  icon: string;
  tooltip: string;
}
interface Account extends AccountDTO {
  AccountEarmarkIcons: Earmark[];
}
export type AccountPreferences = Record<AccountTypeCategoryDTO, Account[]>;

const getEarmarkIcon = (earmarkType: string) => {
  const currentIcon = EARMARK_ICONS.find(({ contains }) => earmarkType.indexOf(contains) > -1);
  return currentIcon ? currentIcon.icon : FALLBACK_ICON;
};

const updateEarmarks = (accounts: AccountDTO[], earmarkOptions: EarmarkOptionsDTO[]) => {
  return accounts.map(account => ({
    ...account,
    AccountEarmarkIcons: (account.AccountEarmark || []).reduce(
      (acc: Earmark[], earmark: AccountEarmarkDTO) => {
        if (earmark.Type.toLowerCase().indexOf(ALL_GOALS) > -1) {
          return [
            ...acc,
            ...earmarkOptions
              .filter(({ value }) => value.toLowerCase() !== ALL_GOALS)
              .map(({ label, value }) => ({
                Tooltip: label,
                tooltip: label,
                Type: value,
                icon: getEarmarkIcon(value.toLocaleLowerCase()),
              })),
          ];
        }

        return [
          ...acc,
          {
            ...earmark,
            tooltip: earmark.Tooltip,
            icon: getEarmarkIcon(earmark.Type.toLocaleLowerCase()),
          },
        ];
      },
      [],
    ),
  })) as Account[];
};

export interface UseAccountPreferencesResponse {
  accounts: AccountPreferences;
  dynamicKey: GroupPossibleKeys;
  ungroupedAccounts: AccountDTO[];
  allowAddManualAccount: boolean;
  options: OptionsDTO;
}

export const useAccountPreferences = (): AsyncComponentHookResult<UseAccountPreferencesResponse> => {
  const clientId = useSelector(currentClientIdSelector) as string;
  const accountQueryInfo = useAccountsQuery();
  const accountOptionsQueryInfo = useAccountsOptionsQuery();
  const dispatch = useDispatch();

  const groupAccountsBy = useSelector(groupAccountsBySelector) as number;

  const data = useMemo(() => {
    if (!accountQueryInfo.data || !accountOptionsQueryInfo.data) {
      return null;
    }

    const { Accounts } = accountQueryInfo.data;

    const {
      Options: { EarmarkOptions: earmarkOptions, AllowAddManualAccount: allowAddManualAccount },
    } = accountOptionsQueryInfo.data;

    const dynamicContent = GroupOptions[
      groupAccountsBy === GroupOptions.AccountTypeCategory
        ? GroupOptions.InstitutionName
        : GroupOptions.AccountTypeCategory
    ] as GroupPossibleKeys;

    const accountsGrouped = groupBy(
      updateEarmarks(
        orderBy(Accounts, account => account.AccountName || account.Description, ['asc']),
        earmarkOptions,
      ),
      GroupOptions[groupAccountsBy],
    ) as AccountPreferences;

    return {
      accounts: {
        ...AccountTypeCategories.reduce(
          (acc, key) => ({
            ...acc,
            [key]: [],
          }),
          {},
        ),
        ...accountsGrouped,
      },

      dynamicKey: dynamicContent,

      ungroupedAccounts: Accounts,

      allowAddManualAccount,

      options: accountOptionsQueryInfo.data.Options,
    };
  }, [accountQueryInfo.data, accountOptionsQueryInfo.data, dispatch, clientId, groupAccountsBy]);

  return {
    ...accountQueryInfo,
    isError: accountQueryInfo.isError || accountOptionsQueryInfo.isError,
    isLoading: accountQueryInfo.isLoading || accountOptionsQueryInfo.isLoading,
    isFetching: accountQueryInfo.isFetching || accountOptionsQueryInfo.isFetching,
    error: accountQueryInfo.error || accountOptionsQueryInfo.error,
    data,
  };
};
